import { MessageCircle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * PersistentChatButton - A floating action button that navigates to the chat page.
 * Visible on all pages except the chat page itself.
 * Positioned bottom-right with mobile-safe zones.
 */
export function PersistentChatButton() {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasUnread, setHasUnread] = useState(false);

  // Hide on chat page (root path "/")
  const isOnChatPage = location.pathname === "/";

  useEffect(() => {
    if (isOnChatPage) return;

    // Check for unread messages (last AI message timestamp vs last viewed)
    const checkUnread = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Get the most recent conversation
        const { data: conversations } = await supabase
          .from("conversations")
          .select("id, updated_at")
          .eq("user_id", user.id)
          .order("updated_at", { ascending: false })
          .limit(1)
          .single();

        if (!conversations) return;

        // Check if there are new messages since we left the chat
        const lastViewedKey = `lastViewedChat_${user.id}`;
        const lastViewed = localStorage.getItem(lastViewedKey);
        
        if (lastViewed) {
          const lastViewedTime = new Date(lastViewed).getTime();
          const conversationTime = new Date(conversations.updated_at).getTime();
          setHasUnread(conversationTime > lastViewedTime);
        }
      } catch (error) {
        console.error("Error checking unread status:", error);
      }
    };

    checkUnread();
  }, [isOnChatPage, location.pathname]);

  // Mark as viewed when navigating to chat
  const handleNavigateToChat = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        localStorage.setItem(`lastViewedChat_${user.id}`, new Date().toISOString());
      }
    } catch (error) {
      console.error("Error marking chat as viewed:", error);
    }
    setHasUnread(false);
    navigate("/");
  };

  if (isOnChatPage) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={handleNavigateToChat}
            size="lg"
            className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 h-14 w-14 rounded-full shadow-lg z-40 hover:scale-110 transition-transform"
            aria-label="Back to Chat"
          >
            <div className="relative">
              <MessageCircle className="h-6 w-6" />
              {hasUnread && (
                <span
                  className="absolute -top-1 -right-1 h-3 w-3 bg-destructive rounded-full animate-pulse"
                  aria-label="Unread messages"
                />
              )}
            </div>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">
          <p>Back to Chat</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
